import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IRegistryEntry, IRegistryMapping } from "./AgentTools.types";

// ─── Registry List ──────────────────────────────────────────────────────────────

export const FETCH_REGISTRY_LIST_PROGRESS = "FETCH_REGISTRY_LIST_PROGRESS";
export const FETCH_REGISTRY_LIST_SUCCESS = "FETCH_REGISTRY_LIST_SUCCESS";
export const FETCH_REGISTRY_LIST_FAILED = "FETCH_REGISTRY_LIST_FAILED";

export const fetchRegistryListProgress = () =>
  action(FETCH_REGISTRY_LIST_PROGRESS);
export const fetchRegistryListSuccess = (
  data: IRegistryEntry[],
  totalRecords: number,
) => action(FETCH_REGISTRY_LIST_SUCCESS, { data, totalRecords });
export const fetchRegistryListFailed = () =>
  action(FETCH_REGISTRY_LIST_FAILED);

export const fetchRegistryListAsync =
  (): ThunkAction<void, IStoreState, {}, AnyAction> => async (dispatch) => {
    dispatch(fetchRegistryListProgress());
    dispatch(saveLoaderProgress());
    try {
      const response = await api.get(`/genai/get-registery?status=ACTIVE&pageNo=1&itemPerPage=50`);
      const data: IRegistryEntry[] = response.data.data || [];
      const totalRecords = response.data.totalRecords || 0;
      dispatch(fetchRegistryListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchRegistryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch registry list",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ─── Registry Mappings ──────────────────────────────────────────────────────────

export const FETCH_REGISTRY_MAPPINGS_PROGRESS =
  "FETCH_REGISTRY_MAPPINGS_PROGRESS";
export const FETCH_REGISTRY_MAPPINGS_SUCCESS =
  "FETCH_REGISTRY_MAPPINGS_SUCCESS";
export const FETCH_REGISTRY_MAPPINGS_FAILED =
  "FETCH_REGISTRY_MAPPINGS_FAILED";

export const fetchRegistryMappingsProgress = () =>
  action(FETCH_REGISTRY_MAPPINGS_PROGRESS);
export const fetchRegistryMappingsSuccess = (data: IRegistryMapping[]) =>
  action(FETCH_REGISTRY_MAPPINGS_SUCCESS, { data });
export const fetchRegistryMappingsFailed = () =>
  action(FETCH_REGISTRY_MAPPINGS_FAILED);

export const fetchRegistryMappingsAsync =
  (companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchRegistryMappingsProgress());
    dispatch(saveLoaderProgress());
    try {
      const response = await api.get(
        `/genai/get-registery-mapping?company_outlet_uuid=${companyOutletUuid}&pageNo=1&itemPerPage=50`,
      );
      const data: IRegistryMapping[] = response.data.data || [];
      console.log("[AgentTools] fetchRegistryMappings - received:", data.length, "mappings", data);
      dispatch(fetchRegistryMappingsSuccess(data));
    } catch (err: any) {
      dispatch(fetchRegistryMappingsFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch registry mappings",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ─── Upsert Registry Mapping ────────────────────────────────────────────────────

export const upsertRegistryMappingAsync =
  (
    data: Partial<IRegistryMapping>,
    onCallback?: (isSuccess: boolean, data?: any) => void,
  ) =>
  async (dispatch: any) => {
    console.log("[AgentTools] upsertRegistryMappingAsync - payload:", JSON.stringify(data));
    dispatch(saveLoaderProgress());
    try {
      const response = await api.post(
        "/genai/upsert-registery-mapping",
        data,
      );
      dispatch(
        showMessage({
          type: "success",
          message: response.data.message || "Mapping updated successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback?.(true, response.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to update registry mapping",
          displayAs: "snackbar",
        }),
      );
      onCallback?.(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ─── Clear State ────────────────────────────────────────────────────────────────

export const CLEAR_AGENT_TOOLS_STATE = "CLEAR_AGENT_TOOLS_STATE";
export const clearAgentToolsState = () => action(CLEAR_AGENT_TOOLS_STATE);
